/* eslint-disable id-length */

/**
 * Module dependencies.
 */

import {
  CategoryScale,
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  defaults as chartDefaults
} from 'chart.js';

import { Graphic } from 'src/types/company-page';
import { Line } from 'react-chartjs-2';
import { color, units } from '@untile/react-components';
import { theme } from 'src/styles/theme';
import React, { ReactElement } from 'react';
import annotations from 'chartjs-plugin-annotation';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import parseInt from 'lodash/parseInt';
import slice from 'lodash/slice';
import styled from 'styled-components';

/**
 * Initialize font.
 */

chartDefaults.font.family = theme.typography.fontFamily.sansSerif;
chartDefaults.font.size = 13;

/**
 * Register chartjs.
 */

ChartJS.register({
  CategoryScale,
  LineElement,
  LinearScale,
  PointElement,
  beforeDraw: annotations
});

/**
 * Constants.
 */

const currentYear = new Date().getFullYear();

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  data: Graphic[],
  labelHelper?: string,
  xAxleLabel?: string,
  yAxleLabel?: string,
  yAxleMaxExtra?: number
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  position: relative;
`;

/**
 * `StyledLine` styled component.
 */

const StyledLine = styled(Line)`
  background-color: ${color('grey100')};
  padding: ${units(5)};
`;

/**
 * `ChartsSection` component.
 */

const ChartsSection = (props: Props): ReactElement => {
  const {
    className,
    data,
    labelHelper,
    xAxleLabel,
    yAxleLabel,
    yAxleMaxExtra
  } = props;

  const graphicData = slice(orderBy(filter(map(data, ({ value, year }: Graphic) => ({
    x: year,
    y: value
  })), ({ x }) => x < currentYear), 'x', 'desc'), 0, 4);

  const annotationData = keyBy(map(graphicData, ({ x, y }, index) => ({
    annotationLabel: `label${index}`,
    content: [labelHelper ? y + labelHelper : y],
    drawTime: 'beforeDatasetsDraw',
    font: {
      size: 15,
      weight: 'bold'
    },
    position: '100%',
    type: 'label',
    xValue: x,
    yValue: y
  })), 'annotationLabel');

  return (
    <Wrapper className={className}>
      <StyledLine
        data={{
          datasets: [{
            backgroundColor: color('primary')({ theme }),
            borderColor: color('primary')({ theme }),
            data: graphicData,
            fill: false,
            pointRadius: 0,
            tension: 0
          }]
        }}
        options={{
          plugins: {
            annotation: {
              annotations: annotationData
            }
          },
          responsive: true,
          scales: {
            x: {
              grid: {
                display: false,
                drawBorder: false
              },
              max: Math.max(...graphicData.map(({ x }) => parseInt(x, 10))),
              min: Math.min(...graphicData.map(({ x }) => parseInt(x, 10))),
              offset: true,
              ticks: {
                callback: (year: number) => year,
                font: {
                  weight: 'lighter'
                },
                stepSize: 1
              },
              title: {
                color: color('black')({ theme }),
                display: true,
                font: {
                  size: 15,
                  weight: 'bold'
                },
                text: xAxleLabel
              },
              type: 'linear'
            },
            y: {
              grid: {
                drawBorder: false
              },
              max: Math.max(...data.map(({ value }) => parseInt(value, 10))) + yAxleMaxExtra,
              min: 0,
              offset: true,
              ticks: {
                display: false
              },
              title: {
                color: color('black')({ theme }),
                display: true,
                font: {
                  size: 15,
                  weight: 'bold'
                },
                text: yAxleLabel
              }
            }
          }
        }}
      />
    </Wrapper>
  );
};

/**
 * Export `ChartsSection` component.
 */

export default ChartsSection;
